import {
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { CheckboxMarkedOutlineIcon, DeleteIcon } from '@atgfr8/icons';
import { Button } from '@components';
import { orderStore, updateOrderState } from '@store/orders';
import {
  LineItemViewModel,
  OrderLineItem,
} from '@typeDefinitions/lineItemTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount } from '@utils/utils';
import { cloneDeep } from 'lodash';
import { Component, Index, Show } from 'solid-js';

import { removeTableRow } from '../../carrier/stops/stopsEditor/utilFunctions';
import cls from '../classes';
import EDICompareChangeStops from './EDICompareChangeStops';

type CurrentChangesProps = {
  order: IOrderViewModel;
  noUpdate: boolean;
};

type UpdateButtonProps = {
  onClick: () => void;
  classes: string;
  noUpdate: boolean;
};

const EDIChangeComparison = (props: CurrentChangesProps) => {
  const headers = ['Type', 'Rate', 'Qty', 'Total', ''];

  const UpdateButton: Component<UpdateButtonProps> = (props) => {
    return !props.noUpdate ? (
      <div class={`${props.classes} text-right mt-auto mb-[2px]`}>
        <Button
          startIcon={
            <CheckboxMarkedOutlineIcon class="text-[#ffffff]" size={20} />
          }
          label="UPDATE"
          variant="contained"
          class="bg-[#388E3C]! updateRateClass"
          onClick={props.onClick}
          dataTestId={formatDataTestId('UPDATE', 'button')}
        />
      </div>
    ) : null;
  };

  const updateLineItems = (
    key: string,
    value: string | number,
    index: number,
  ) => {
    const lineItems = [...orderStore.order.lineItems];
    if (lineItems[index].operationType === 'None') {
      lineItems[index] = {
        ...lineItems[index],
        [key]: value,
        operationType: 'Update',
      };
    } else {
      lineItems[index] = { ...lineItems[index], [key]: value };
    }
    updateOrderState({ lineItems: lineItems });
  };

  const renderActionColumn = (id: number, rowData: LineItemViewModel) => {
    if (props.noUpdate) {
      return (
        <IconButton
          dataTestId="delete-edi-line-item-button"
          onClick={() => {
            removeTableRow(id, orderStore.order, 'order', 'lineItems');
          }}
        >
          <DeleteIcon class="text-[#B00020] cursor-pointer" />
        </IconButton>
      );
    }
    return (
      <UpdateButton
        onClick={() => {
          copyLineItems(rowData);
        }}
        classes="w-[50%]"
        noUpdate={props.noUpdate}
      />
    );
  };

  const copyLineItems = (lineItem: LineItemViewModel) => {
    const originalLineItemIndex = orderStore.order.lineItems.findIndex(
      (x) => x.type === lineItem.type && x.operationType !== 'Delete',
    );
    // If line item exists, update with new changes
    if (originalLineItemIndex !== -1) {
      const lineItems = [...orderStore.order.lineItems];
      lineItems[originalLineItemIndex] = {
        ...lineItems[originalLineItemIndex],
        rate: lineItem.rate || null,
        quantity: lineItem.quantity as number,
        operationType: 'Update',
      };
      updateOrderState({ lineItems: lineItems });
    }
    // otherwise, add it to the list of line items
    else {
      const clonedLineItem = cloneDeep(lineItem as unknown as OrderLineItem);
      const lineItems = [...orderStore.order.lineItems];
      lineItems.push({
        ...clonedLineItem,
        operationType: 'Insert',
      });
      updateOrderState({ lineItems: lineItems });
    }
  };

  return (
    <div class={cls.container}>
      <div class={cls.containerHeading}>
        <span class={cls.containerHeadingText}>
          {props.noUpdate ? 'Current' : 'Load Tender Changes'}
        </span>
      </div>
      <Divider class="mb-[10px]!" />
      <div class={cls.currentChangesSubHeading}>
        <span class="text-lg font-bold">LineItems</span>
      </div>
      <div>
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {headers.map((item) => (
                  <TableHeaderCell class="text-black">{item}</TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <Index each={props.order.lineItems}>
                {(rowData, index) => (
                  <Show when={rowData().operationType !== 'Delete'}>
                    <TableRow>
                      <TableCell>{rowData().type}</TableCell>
                      <TableCell>
                        <TextField
                          label=""
                          type="number"
                          significantDigits={3}
                          placeholder="Rate"
                          value={rowData().rate || ''}
                          onChange={(value: string | number | Event | null) => {
                            if (typeof value === 'number' || value === null) {
                              updateLineItems(
                                'rate',
                                value === null ? 0 : value,
                                index,
                              );
                            }
                          }}
                          error={Boolean(
                            orderStore.orderFormError &&
                            orderStore.orderFormError[
                              `lineItems[${index}].rate`
                            ],
                          )}
                          class={'bg-white rounded-md w-[100px]!'}
                          disabled={!props.noUpdate}
                          dataTestId="rate-textInput"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          label=""
                          type="number"
                          significantDigits={3}
                          placeholder="Quantity"
                          value={rowData().quantity}
                          onChange={(value: string | number | Event | null) => {
                            if (typeof value === 'number' || value === null) {
                              updateLineItems(
                                'quantity',
                                value === null ? 0 : value,
                                index,
                              );
                            }
                          }}
                          error={Boolean(
                            orderStore.orderFormError &&
                            orderStore.orderFormError[
                              `lineItems[${index}].quantity`
                            ],
                          )}
                          class={'bg-white rounded-md w-[100px]!'}
                          disabled={!props.noUpdate}
                          dataTestId="quantity-textInput"
                        />
                      </TableCell>
                      <TableCell>
                        <span class="font-bold inline-block w-full bg-[#C8E6C9] p-4">
                          ={' '}
                          {formatAmount(
                            Number(rowData().quantity) * Number(rowData().rate),
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        {renderActionColumn(
                          index,
                          rowData() as LineItemViewModel,
                        )}
                      </TableCell>
                    </TableRow>
                  </Show>
                )}
              </Index>
            </TableBody>
          </Table>
        </div>
      </div>

      <div class={cls.currentChangesSubHeading}>
        <span class="text-lg font-bold">Stops</span>
      </div>
      <div>
        <EDICompareChangeStops noUpdate={props.noUpdate} order={props.order} />
      </div>
      <Divider />
    </div>
  );
};

export default EDIChangeComparison;
